import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Navigation, Sparkles, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { isTourActiveGlobal } from '@/hooks/useTourController';
import { getLocalStorageState } from '@/hooks/useFirstRunState';
import { diagLog } from '@/utils/diagnosticSession';

interface MobileNavIntroProps {
  isOpen: boolean;
  onDismiss: () => void;
}

/**
 * MobileNavIntro - Shows navigation hub intro when menu first opens
 * 
 * CRITICAL: Must NOT show during first-run flow (onboarding, welcome, or tour)
 * The FirstTimeAppTour already covers menu education - this is for returning users only.
 */
export const MobileNavIntro: React.FC<MobileNavIntroProps> = ({ isOpen, onDismiss }) => {
  const [hasSeenIntro, setHasSeenIntro] = useState(() => {
    return localStorage.getItem('therappi-nav-intro-seen') === 'true';
  });
  
  const [showIntro, setShowIntro] = useState(false);
  
  // Check if user is still in first-run flow
  const isInFirstRunFlow = (): boolean => {
    // Tour is active/preparing/transitioning
    if (isTourActiveGlobal()) return true;
    
    // Welcome hasn't been seen yet (user hasn't passed typewriter)
    const { welcomeSeen, tourSeen } = getLocalStorageState();
    if (!welcomeSeen || !tourSeen) return true;
    
    return false;
  };
  
  useEffect(() => {
    // CRITICAL: Never show during first-run flow (onboarding, welcome, tour)
    // The tour has its own menu walkthrough - this would be duplicate/confusing
    if (isInFirstRunFlow()) {
      console.log('[MobileNavIntro] Blocked - still in first-run flow');
      return;
    }
    
    if (isOpen && !hasSeenIntro) {
      setShowIntro(true);
    }
  }, [isOpen, hasSeenIntro]);
  
  const handleDismiss = () => {
    setShowIntro(false);
    setHasSeenIntro(true);
    localStorage.setItem('therappi-nav-intro-seen', 'true');
    onDismiss();
  };
  
  // DIAGNOSTIC: Log render decision
  const inFirstRun = isInFirstRunFlow();
  const { welcomeSeen, tourSeen } = getLocalStorageState();
  
  diagLog('MobileNavIntro', 'RENDER', {
    isOpen,
    hasSeenIntro,
    showIntro,
    inFirstRun,
    isTourActive: isTourActiveGlobal(),
    welcomeSeen,
    tourSeen,
    willRender: !inFirstRun && showIntro
  });

  // HARD GUARD: Never render during first-run flow
  if (inFirstRun) {
    return null;
  }
  
  if (!showIntro) return null;
  
  return (
    <AnimatePresence>
      {showIntro && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={handleDismiss}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: -20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-gradient-to-br from-background via-background to-primary/10 rounded-2xl p-6 max-w-sm w-full shadow-2xl border border-primary/20"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4"
            >
              <Navigation className="w-8 h-8 text-primary" />
            </motion.div>
            
            {/* Title */}
            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-xl font-semibold text-center mb-2 text-foreground"
            >
              Your Navigation Hub
            </motion.h2>
            
            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-center text-muted-foreground mb-4 text-sm"
            >
              This menu gives you access to <span className="text-primary font-medium">every feature</span> in Therappi<sup className="text-[0.6em]">®</sup>. 
              Explore wellness tools, track your progress, and connect with AI support - all from here.
            </motion.p>
            
            {/* Features list */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="space-y-2 mb-5"
            >
              {[
                'AI Reflection & Daily Check-Ins',
                'Wellness Tools & Breathwork',
                'Tracking & Insights',
                'Courses & Community'
              ].map((feature, index) => (
                <motion.div
                  key={feature}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="flex items-center gap-2 text-sm text-foreground/80"
                >
                  <Sparkles className="h-4 w-4 text-primary flex-shrink-0" />
                  <span>{feature}</span>
                </motion.div>
              ))}
            </motion.div>
            
            {/* Tip */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="bg-primary/10 rounded-lg p-3 mb-4"
            >
              <p className="text-xs text-primary/90 text-center">
                <strong>Tip:</strong> Star your favourites for quick access!
              </p>
            </motion.div>
            
            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
            >
              <Button 
                onClick={handleDismiss}
                className="w-full gap-2"
              >
                Start Exploring
                <ChevronRight className="h-4 w-4" />
              </Button>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
